import React, { useEffect, useRef, useState } from "react";
import { Download, Share2, Link2, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CARD_THEMES, drawShareCard, downloadShareCard, shareCard } from "@/lib/shareCard";
import { track } from "@/lib/analytics";

export const ShareCardModal = ({ card, shareUrl, open, onClose, heading = "Share this day", analyticsName = "share_card", testPrefix = "share-card", defaultTheme = "ink", shareText = "" }) => {
  const previewRef = useRef(null);
  const [theme, setTheme] = useState(defaultTheme);
  const [busy, setBusy] = useState("");
  const cardData = card && { ...card, theme };

  useEffect(() => {
    if (!open || !card) return undefined;
    let tries = 0, timer;
    const attempt = () => {
      if (previewRef.current) drawShareCard(previewRef.current, cardData);
      else if (tries++ < 20) timer = setTimeout(attempt, 50);
    };
    attempt();
    return () => clearTimeout(timer);
  }, [open, card, theme]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      const result = await fn();
      if (result !== "unsupported") track(`${analyticsName}_${kind}`, { category: "share", theme, title: card.title });
      return result;
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Something went wrong — try Download instead");
    } finally {
      setBusy("");
    }
  };

  const download = () => run("download", async () => { await downloadShareCard(cardData); toast.success("Card downloaded"); });
  const share = () => run("share", async () => {
    const r = await shareCard(cardData, shareUrl);
    if (r === "unsupported") { await downloadShareCard(cardData); toast.success("Sharing isn't supported here — card downloaded instead"); }
    return r;
  });
  const copyLink = () => run("copy", async () => { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); });
  const openIntent = (kind, url) => run(kind, async () => { window.open(url, "_blank", "noopener,noreferrer"); return true; });
  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText || card?.title || ""} ${shareUrl}`)}`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText || card?.title || "")}&url=${encodeURIComponent(shareUrl)}`;

  if (!card) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`${testPrefix}-modal`} className="max-w-lg bg-paper border-ink p-0 overflow-hidden [&>button]:hidden max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 pt-5">
          <div>
            <DialogTitle className="text-xl font-bold text-ink">{heading}</DialogTitle>
            <DialogDescription className="text-sm text-ink-muted">A square card with the OddlyTrue seal — Share sends the image straight to Instagram, WhatsApp or X on your phone; Download saves the PNG.</DialogDescription>
          </div>
          <button type="button" data-testid={`${testPrefix}-close`} onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full border border-[#e5e7eb] bg-white text-ink hover:border-gold flex items-center justify-center shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 pt-4">
          <canvas ref={previewRef} data-testid={`${testPrefix}-preview`} className="w-full aspect-square rounded-xl border border-[#e5e7eb] shadow-sm" />
        </div>
        <div className="px-6 pt-4 flex items-center gap-2" role="radiogroup" aria-label="Card style">
          <span className="text-[11px] uppercase tracking-widest text-ink-muted mr-1">Style</span>
          {Object.values(CARD_THEMES).map((t) => (
            <button key={t.id} type="button" role="radio" aria-checked={theme === t.id} data-testid={`${testPrefix}-style-${t.id}`} onClick={() => setTheme(t.id)}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[12px] font-semibold transition-colors ${theme === t.id ? "border-gold bg-white text-ink" : "border-[#e5e7eb] bg-white text-ink-muted hover:border-gold"}`}>
              <span className="w-3 h-3 rounded-full border border-[#e5e7eb]" style={{ background: t.bg }} />{t.label}
            </button>
          ))}
        </div>
        <div className="px-6 py-5 flex flex-wrap gap-2">
          <button type="button" data-testid={`${testPrefix}-share`} onClick={share} disabled={!!busy} className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink disabled:opacity-60 transition-colors">
            <Share2 className="w-4 h-4" /> {busy === "share" ? "Preparing…" : "Share"}
          </button>
          <button type="button" data-testid={`${testPrefix}-download`} onClick={download} disabled={!!busy} className="inline-flex items-center gap-2 px-5 h-11 rounded-full border border-ink text-ink text-sm font-semibold hover:bg-ink hover:text-white disabled:opacity-60 transition-colors">
            <Download className="w-4 h-4" /> {busy === "download" ? "Preparing…" : "Download PNG"}
          </button>
          {shareText && (
            <>
              <button type="button" data-testid={`${testPrefix}-whatsapp`} onClick={() => openIntent("whatsapp", waUrl)} disabled={!!busy} className="inline-flex items-center gap-2 px-4 h-11 rounded-full border border-[#25D366] text-ink text-sm font-semibold hover:bg-[#25D366] hover:text-white disabled:opacity-60 transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
              <button type="button" data-testid={`${testPrefix}-x`} onClick={() => openIntent("x", xUrl)} disabled={!!busy} className="inline-flex items-center gap-2 px-4 h-11 rounded-full border border-ink text-ink text-sm font-semibold hover:bg-ink hover:text-white disabled:opacity-60 transition-colors">
                <span className="font-black text-[15px] leading-none">𝕏</span> Post
              </button>
            </>
          )}
          <button type="button" data-testid={`${testPrefix}-copy`} onClick={copyLink} disabled={!!busy} className="inline-flex items-center gap-2 px-4 h-11 rounded-full text-ink-muted text-sm font-semibold hover:text-gold transition-colors">
            <Link2 className="w-4 h-4" /> Copy link
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
